import { memo, useState } from "react";
import { CalendarView } from "src/utils/constants";
import { IMeeting } from "..";
import { useMantineTheme, Flex, Box } from "@mantine/core";
import { isCurrentDateInRange } from "src/utils/dateHelper";
import Switch from "src/components/Switch/Switch";
import ListItem from "./ListItem";

type IProps = {
  view: CalendarView;
  meetings: IMeeting;
  handleModal: (id?: string | null, start?: Date) => void;
  dateRange: [Date, Date];
};

function groupAndSortByDate(
  data: IMeeting,
  showPast: boolean
): {
  [date: string]: IMeeting;
} {
  //Group meeting By dates
  const groupedData: { [date: string]: IMeeting } = {};
  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0); // search for whole day

  data.forEach((obj) => {
    const date = obj.startAt.split("T")[0];
    if (showPast || new Date(obj.startAt) >= currentDate) {
      if (!groupedData[date]) {
        groupedData[date] = [];
      }
      groupedData[date].push(obj);
    }
  });

  // sort by dates
  const sortedData: [string, IMeeting][] = Object.entries(groupedData).sort(
    (a, b) => (new Date(a[0]) > new Date(b[0]) ? 1 : -1)
  );

  return sortedData.reduce((acc: any, [date, events]) => {
    acc[date] = events;
    return acc;
  }, {});
}

function CalendarList({ meetings, handleModal, view, dateRange }: IProps) {
  const theme = useMantineTheme();
  const [splitRow, setsplitRow] = useState(true);
  const [showPast, setshowPast] = useState(true);
  const meetingDates = groupAndSortByDate(meetings, showPast);
  let dateInRange =
    isCurrentDateInRange(dateRange[0], dateRange[1]) &&
    view !== CalendarView.Day;

  const dates = Object.keys(meetingDates);
  const middleIndex = Math.ceil(dates.length / 2);
  const firstRowDates = dates.slice(0, middleIndex);
  const secondRowDates = dates.slice(middleIndex);

  return (
    <>
      {(view === CalendarView.Month || dateInRange) && (
        <Flex mt={"sm"} style={{ gap: 25 }} direction="row-reverse">
          {view === CalendarView.Month && (
            <Switch
              onChange={() => setsplitRow(!splitRow)}
              checked={splitRow}
              labelPosition="left"
              label={`Split Row`}
            />
          )}

          {dateInRange && (
            <Switch
              labelPosition="left"
              label={`Show Past events`}
              onChange={() => setshowPast(!showPast)}
              checked={showPast}
            />
          )}
        </Flex>
      )}

      {splitRow && view === CalendarView.Month ? (
        <Flex justify={"space-between"} gap={10}>
          <Box style={{ width: "50%" }}>
            {firstRowDates.map((date) => (
              <ListItem
                meetingDates={meetingDates}
                handleModal={handleModal}
                date={date}
                key={date}
              />
            ))}
          </Box>
          <Box style={{ width: "50%" }}>
            {secondRowDates.map((date) => (
              <ListItem
                meetingDates={meetingDates}
                handleModal={handleModal}
                date={date}
                key={date}
              />
            ))}
          </Box>
        </Flex>
      ) : (
        <>
          {Object.keys(meetingDates).map((date) => (
            <ListItem
              meetingDates={meetingDates}
              handleModal={handleModal}
              date={date}
              key={date}
            />
          ))}
        </>
      )}
    </>
  );
}

export default memo(CalendarList);
